import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';

import {
    makeStyles,
    Typography,
} from '@material-ui/core';

import TrackedLink from './TrackedLink';
import { activityLabels } from '../constants';
import { scrollerWidth } from '../../app/style/theme';
import { parseBoolString } from '../../youwell-common/stringUtils';

const useStyles = makeStyles(theme => ({
    root: {
        background: theme.palette.primary.main,
        padding: theme.spacing(4, 2),
        paddingRight: theme.spacing(2) + scrollerWidth,

        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(4, 0),
            paddingRight: scrollerWidth,
        },

        '$brightBackground&': {
            background: theme.youwell.colors.secondaryBackground,
        },
    },
    brightBackground: { },
    menuItem: {
        display: 'flex',
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        borderLeft: theme.youwell.borders.medium,
        borderColor: 'transparent',

        '&$subtask': {
            paddingLeft: theme.spacing(3),
        },

        '&hiddenSubtask': {
            display: 'none',
        },
    },
    subtask: {},
    hiddenSubtask: {},
    title: {
        color: theme.palette.common.white,
        marginBottom: theme.spacing(2),
        fontSize: '1rem',

        '$brightBackground &': {
            color: theme.youwell.colors.primary,
        },
    },
    current: {
        borderColor: theme.youwell.colors.highlight,

        '& $text': {
            color: theme.youwell.colors.highlight,
            fontWeight: theme.typography.fontWeightMedium,

            '$brightBackground &': {
                color: theme.palette.text.primary,
            },
        },
    },
    text: {
        display: 'flex',
        alignItems: 'center',
    },
    previous: {
        color: theme.youwell.colors.grayLight,

        '$brightBackground &': {
            color: theme.youwell.colors.grayDark,
        },
    },
    whiteText: {
        color: theme.palette.common.white,

        '$brightBackground &': {
            color: theme.palette.text.primary,
        },
    },
    notify: {
        width: 8,
        height: 8,
        borderRadius: '100%',
        marginLeft: theme.spacing(1),
        display: 'block',
        background: theme.youwell.colors.highlight,
    },
}), { name: 'SideMenu' });

const SideMenu = ({ menu, className, activeSideMenuDivRef }) => {
    const classes = useStyles();
    const translate = useSelector(state => getTranslate(state.localize));
    const clientConfig = useSelector(state => state.auth.clientConfig);

    if (!menu) {
        return null;
    }

    return (
        <div className={classnames(classes.root, className, { [classes.brightBackground]: clientConfig.appSetup.features.useSecondaryBackroundInSideMenu })}>
            <Typography variant="subtitle2" className={classnames(classes.menuItem, classes.title)}>
                {menu.title}
            </Typography>

            {menu.menuItems.map((item, i) => (
                <div
                    key={item.url || item.text}
                    ref={i === menu.currentIndex ? activeSideMenuDivRef : undefined}
                    className={classnames(classes.menuItem, {
                        [classes.current]: i === menu.currentIndex,
                        [classes.subtask]: parseBoolString(item.settings?.subtask),
                        [classes.hiddenSubtask]: parseBoolString(item.settings?.hiddenInMenu),
                    })}
                    title={item.notify ? translate('task.unfinishedTask') : null}
                >
                    {item.url ? (
                        <TrackedLink to={item.url} label={`${activityLabels.sidemenu.link}/${i + 1}`}>
                            <Typography variant="body2" className={classnames(classes.text, i > menu.currentIndex ? classes.whiteText : classes.previous)}>
                                {item.text}
                                {item.notify && (<span className={classes.notify} />)}
                            </Typography>
                        </TrackedLink>
                    ) : (
                        <Typography variant="body2" className={classnames(classes.text, i > menu.currentIndex ? classes.whiteText : classes.previous)}>
                            {item.text}
                            {item.notify && (<span className={classes.notify} />)}
                        </Typography>
                    )}
                </div>
            ))}
        </div>
    );
};

SideMenu.propTypes = {
    menu: PropTypes.object,
    className: PropTypes.string,
    activeSideMenuDivRef: PropTypes.object,
};

SideMenu.defaultProps = {
    menu: null,
    className: null,
    activeSideMenuDivRef: undefined,
};

export default SideMenu;
